/* eslint-disable react/no-this-in-sfc */
import { colord } from "colord";
import format from "date-fns/format";
import Highcharts from "highcharts";
import { HighchartsReactRefObject } from "highcharts-react-official";
import { useMemo, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { useAppVariant } from "@jugl-web/utils";
import { normalizeChartValue } from "../../utils";
import { WidgetTooltip } from "../WidgetTooltip";
import { TopicDistributionDataEntry } from "./types";

type TopicDistributionTreeMapTooltipPoint = Highcharts.Point & {
  node?: { level: number };
  custom?: { chatsCount: number };
};

export const useTopicDistributionChart = (
  data: TopicDistributionDataEntry[]
) => {
  const chartRef = useRef<HighchartsReactRefObject>(null);
  const { isMobile } = useAppVariant();

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        type: "treemap",
        spacing: [0, 0, 0, 0],
        style: {
          fontFamily: "Poppins",
        },
      },
      title: {
        text: undefined,
      },
      accessibility: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      tooltip: {
        useHTML: true,
        followPointer: false,
        followTouchMove: false,
        backgroundColor: "transparent",
        borderWidth: 0,
        shadow: false,
        padding: 0,
        style: { whiteSpace: "normal" },
        formatter() {
          const point = this as TopicDistributionTreeMapTooltipPoint;

          if (point.node?.level !== 2) {
            return "";
          }

          const chatsCount = point.custom?.chatsCount ?? 0;
          const subtitle = format(new Date(), "MMMM, yyyy");

          return ReactDOMServer.renderToStaticMarkup(
            <WidgetTooltip
              title={point.name}
              subtitle={subtitle}
              content={
                <div>
                  <span className="text-dark text-base font-semibold">
                    {chatsCount}
                  </span>{" "}
                  <span className="text-dark-900 font-sm font-normal">
                    {chatsCount === 1 ? "session" : "sessions"}
                  </span>
                </div>
              }
            />
          );
        },
      },
      plotOptions: {
        treemap: {
          allowTraversingTree: true,
          alternateStartingDirection: false,
          layoutAlgorithm: "squarified",
          groupPadding: isMobile ? 2 : 4,
          states: {
            hover: {
              brightness: -0.1,
            },
          },
        },
        series: {
          animation: false,
        },
      },
      series: [
        {
          name: "All Topics",
          type: "treemap",
          data: data.map((item) => {
            if (item.type === "topic") {
              return {
                id: item.name,
                name: item.name,
                color: item.color,
              };
            }

            return {
              name: item.name,
              parent: item.parent,
              value: normalizeChartValue(item.value, isMobile),
              color: item.color,
              custom: { chatsCount: item.chatsCount },
            };
          }),
          levels: [
            {
              level: 1,
              borderWidth: 0,
              borderColor: "transparent",
              dataLabels: {
                enabled: true,
                headers: true,
                align: "left",
                padding: isMobile ? 1 : 2,
                style: {
                  fontSize: isMobile ? "11px" : "13px",
                  cursor: "pointer",
                },
                formatter() {
                  const series = this.series as Highcharts.Series & {
                    rootNode: string;
                  };

                  if (series.rootNode !== "") {
                    return "";
                  }

                  return this.name;
                },
              },
            },
            {
              level: 2,
              borderWidth: isMobile ? 0.5 : 1,
              borderColor: "#00000020",
              dataLabels: {
                enabled: true,
                align: "left",
                verticalAlign: "top",
                padding: isMobile ? 1 : 4,
                style: {
                  fontSize: isMobile ? "12px" : "15px",
                  fontWeight: "400",
                  textOutline: "none",
                },
                formatter() {
                  const point = this as Highcharts.Point & {
                    custom?: { chatsCount: number };
                    shapeArgs?: { width?: number; height?: number };
                  };

                  const shadowColorRGB =
                    typeof point.color === "string"
                      ? colord(point.color).brightness() < 0.4
                        ? "0, 0, 0"
                        : "255, 255, 255"
                      : "0, 0, 0";

                  const chatsCount = point.custom?.chatsCount ?? 0;
                  const sessionsLabel = `${chatsCount} ${
                    chatsCount === 1 ? "session" : "sessions"
                  }`;
                  const tileWidth = point.shapeArgs?.width ?? 0;
                  const tileHeight = point.shapeArgs?.height ?? 0;
                  const estimatedTitleWidth = point.name.length * 6.4;
                  const estimatedSessionsWidth = sessionsLabel.length * 5.8;
                  const hasRoomForStackedLayout =
                    tileWidth >=
                      Math.max(estimatedTitleWidth, estimatedSessionsWidth) +
                        12 && tileHeight >= 34;
                  const shouldUseInlineLayout =
                    isMobile && !hasRoomForStackedLayout;

                  return ReactDOMServer.renderToStaticMarkup(
                    <div>
                      <span
                        style={{
                          textShadow: `0 0 4px rgba(${shadowColorRGB}, 0.40)`,
                          fontWeight: 600,
                        }}
                      >
                        {point.name}
                      </span>
                      {shouldUseInlineLayout ? " · " : <br />}
                      <span
                        style={{
                          textShadow: `0 0 1px rgba(${shadowColorRGB}, 0.40)`,
                          fontSize: isMobile ? 10 : 11,
                        }}
                      >
                        {sessionsLabel}
                      </span>
                    </div>
                  );
                },
              },
            },
          ],
          breadcrumbs: {
            floating: false,
            position: {
              align: "left",
              verticalAlign: "top",
            },
            buttonSpacing: 0,
            separator: {
              text: "&nbsp;>&nbsp;",
              style: {
                width: 50,
                color: "#C5C5CA",
              },
            },
            buttonTheme: {
              style: {
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "400",
                color: "#5A6367",
                cursor: "pointer",
                backgroundColor: "white",
                background: "white",
              },
            },
          },
        },
      ],
    }),
    [data, isMobile]
  );

  const isEmpty = data.length === 0;

  return {
    chartRef,
    options,
    isEmpty,
  };
};
